import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { XMarkIcon, PlusIcon, TrashIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import api from '../lib/api'
import type { DatasetDetail, FilterGroup, PaginatedResponse, SuppressionList, Export } from '../types'
import toast from 'react-hot-toast'

interface ExportModalProps {
  dataset: DatasetDetail
  filter: FilterGroup | null
  sqlQuery?: string | null  // SQL from NL query
  onClose: () => void
}

interface SuppressionMappingState {
  suppression_list_id: string
  suppression_column: string
  dataset_column: string
  match_type: 'exact' | 'contains' | 'domain'
}

export default function ExportModal({ dataset, filter, sqlQuery, onClose }: ExportModalProps) {
  const queryClient = useQueryClient()
  
  const [name, setName] = useState(`${dataset.name} Export`)
  const [campaignName, setCampaignName] = useState('')
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    dataset.columns.map(c => c.name)
  )
  const [suppressionMappings, setSuppressionMappings] = useState<SuppressionMappingState[]>([])
  const [expandedMappings, setExpandedMappings] = useState<Set<number>>(new Set())
  
  // Split export state
  const [splitOption, setSplitOption] = useState<string>('none')
  const [customRowsPerFile, setCustomRowsPerFile] = useState<string>('10000')
  
  // Fetch suppression lists (with columns for ready lists)
  const { data: suppressionsData } = useQuery({
    queryKey: ['suppression-lists'],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<SuppressionList>>('/suppression-lists')
      return res.data
    },
  })
  
  // Only show ready suppression lists
  const readySuppressionLists = suppressionsData?.items.filter(list => list.status === 'ready') || []
  
  // Fetch columns for selected suppression lists
  const [suppressionListDetails, setSuppressionListDetails] = useState<Record<string, SuppressionList>>({})
  
  useEffect(() => {
    const fetchColumns = async () => {
      for (const mapping of suppressionMappings) {
        if (!suppressionListDetails[mapping.suppression_list_id]) {
          try {
            const res = await api.get<SuppressionList>(`/suppression-lists/${mapping.suppression_list_id}`)
            setSuppressionListDetails(prev => ({
              ...prev,
              [mapping.suppression_list_id]: res.data,
            }))
          } catch (e) {
            console.error('Failed to fetch suppression list details', e)
          }
        }
      }
    }
    fetchColumns()
  }, [suppressionMappings])
  
  const exportMutation = useMutation({
    mutationFn: async () => {
      // Validate mappings
      const validMappings = suppressionMappings.filter(
        m => m.suppression_list_id && m.suppression_column && m.dataset_column
      )
      
      // Calculate rows_per_file based on split option
      let rowsPerFile: number | undefined
      if (splitOption === '1k') rowsPerFile = 1000
      else if (splitOption === '10k') rowsPerFile = 10000
      else if (splitOption === '50k') rowsPerFile = 50000
      else if (splitOption === 'custom') {
        const customValue = parseInt(customRowsPerFile)
        if (customValue > 0) rowsPerFile = customValue
      }
      
      const payload = {
        dataset_id: dataset.id,
        name,
        campaign_name: campaignName || undefined,
        columns: selectedColumns,
        filter_definition: filter || undefined,
        sql_query: sqlQuery || undefined,
        suppression_mappings: validMappings.length > 0 ? validMappings : undefined,
        rows_per_file: rowsPerFile,
      }
      
      console.log('Creating export with:', payload)
      const res = await api.post<Export>('/exports', payload)
      return res.data
    },
    onSuccess: () => {
      toast.success('Export started! Check the Exports page for progress.')
      queryClient.invalidateQueries({ queryKey: ['exports'] })
      onClose()
    },
    onError: (error: unknown) => {
      console.error('Export error:', error)
      const message = error instanceof Error ? error.message : 'Unknown error'
      toast.error(`Failed to create export: ${message}`)
    },
  })
  
  const toggleColumn = (col: string) => {
    setSelectedColumns(prev => 
      prev.includes(col) 
        ? prev.filter(c => c !== col)
        : [...prev, col]
    )
  }
  
  const addSuppressionMapping = () => {
    const newIndex = suppressionMappings.length
    setSuppressionMappings(prev => [
      ...prev,
      {
        suppression_list_id: '',
        suppression_column: '',
        dataset_column: '',
        match_type: 'exact',
      },
    ])
    setExpandedMappings(prev => new Set(prev).add(newIndex))
  }
  
  const removeSuppressionMapping = (index: number) => {
    setSuppressionMappings(prev => prev.filter((_, i) => i !== index))
    setExpandedMappings(prev => {
      const next = new Set(prev)
      next.delete(index)
      return next
    })
  }
  
  const updateSuppressionMapping = (index: number, updates: Partial<SuppressionMappingState>) => {
    setSuppressionMappings(prev => prev.map((m, i) => 
      i === index ? { ...m, ...updates } : m
    ))
  }
  
  const toggleMappingExpanded = (index: number) => {
    setExpandedMappings(prev => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }
  
  const getSuppressionListName = (id: string) => {
    const list = readySuppressionLists.find(l => l.id === id)
    return list?.name || 'Select list...'
  }
  
  const getSuppressionColumns = (listId: string) => {
    const detail = suppressionListDetails[listId]
    return detail?.columns || []
  }
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Export Data</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          {/* Body */}
          <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Export name */}
            <div>
              <label className="label">Export Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
              />
            </div>
            
            {/* Campaign name */}
            <div>
              <label className="label">
                Campaign Name <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                className="input"
                placeholder="For tracking purposes"
              />
            </div>
            
            {/* Filter info */}
            {filter && (filter.and || filter.or) && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-800 mb-2">
                  Filter Applied ({filter.and ? 'AND' : 'OR'})
                </p>
                <ul className="text-sm text-blue-700 space-y-1">
                  {(filter.and || filter.or || []).map((cond, idx) => {
                    if ('column' in cond) {
                      return (
                        <li key={idx} className="flex items-center gap-1 flex-wrap">
                          <span className="font-medium">{cond.column}</span>
                          <span className="text-blue-500">{cond.op}</span>
                          {cond.value !== undefined && (
                            <span className="bg-blue-100 px-1.5 py-0.5 rounded">
                              {String(cond.value)}
                            </span>
                          )}
                        </li>
                      )
                    }
                    return <li key={idx} className="text-xs text-blue-500">(nested group)</li>
                  })}
                </ul>
              </div>
            )}
            
            {/* Column selection */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="label mb-0">Columns to Export</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedColumns(dataset.columns.map(c => c.name))}
                    className="text-xs text-primary-600 hover:text-primary-700"
                  >
                    Select All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    type="button"
                    onClick={() => setSelectedColumns([])}
                    className="text-xs text-primary-600 hover:text-primary-700"
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              <div className="border rounded-lg p-3 max-h-40 overflow-y-auto">
                <div className="flex flex-wrap gap-2">
                  {dataset.columns.map((col) => (
                    <label
                      key={col.id}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm cursor-pointer transition-colors ${
                        selectedColumns.includes(col.name)
                          ? 'bg-primary-100 text-primary-700 border border-primary-200'
                          : 'bg-gray-100 text-gray-700 border border-transparent hover:bg-gray-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedColumns.includes(col.name)}
                        onChange={() => toggleColumn(col.name)}
                        className="sr-only"
                      />
                      {col.name}
                    </label>
                  ))}
                </div>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {selectedColumns.length} of {dataset.columns.length} columns selected
              </p>
            </div>
            
            {/* Split Export Option */}
            <div>
              <label className="label">Split Export</label>
              <select
                value={splitOption}
                onChange={(e) => setSplitOption(e.target.value)}
                className="input"
              >
                <option value="none">Don't split (single file)</option>
                <option value="1k">1,000 rows per file</option>
                <option value="10k">10,000 rows per file</option>
                <option value="50k">50,000 rows per file</option>
                <option value="custom">Custom...</option>
              </select>
              
              {splitOption === 'custom' && (
                <div className="mt-2">
                  <label className="label text-xs">Rows per file</label>
                  <input
                    type="number"
                    min="1"
                    value={customRowsPerFile}
                    onChange={(e) => setCustomRowsPerFile(e.target.value)}
                    className="input"
                    placeholder="Enter number of rows per file"
                  />
                </div>
              )}
              
              {splitOption !== 'none' && (
                <p className="mt-1 text-xs text-gray-500">
                  Export will be split into multiple CSV files and packaged into a ZIP archive
                </p>
              )}
            </div>
            
            {/* Suppression mappings */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="label mb-0">Suppression Lists</label>
                {readySuppressionLists.length > 0 && (
                  <button
                    onClick={addSuppressionMapping}
                    className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Suppression
                  </button>
                )}
              </div>
              
              {readySuppressionLists.length === 0 ? (
                <div className="border rounded-lg p-3 bg-gray-50">
                  <p className="text-sm text-gray-500">
                    No suppression lists available.
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Upload CSV files in the Suppressions section to exclude records from exports.
                  </p>
                </div>
              ) : suppressionMappings.length === 0 ? (
                <div className="border rounded-lg p-3 bg-gray-50">
                  <p className="text-sm text-gray-500">
                    No suppressions applied. Click "Add Suppression" to exclude records.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {suppressionMappings.map((mapping, index) => (
                    <div key={index} className="border rounded-lg overflow-hidden">
                      {/* Mapping header */}
                      <div 
                        className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer"
                        onClick={() => toggleMappingExpanded(index)}
                      >
                        <div className="flex items-center gap-2">
                          <ChevronDownIcon 
                            className={`h-4 w-4 transition-transform ${expandedMappings.has(index) ? 'rotate-180' : ''}`}
                          />
                          <span className="text-sm font-medium">
                            {mapping.suppression_list_id 
                              ? getSuppressionListName(mapping.suppression_list_id)
                              : 'New Suppression'}
                          </span>
                          {mapping.suppression_column && mapping.dataset_column && (
                            <span className="text-xs text-gray-500">
                              ({mapping.suppression_column} → {mapping.dataset_column})
                            </span>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            removeSuppressionMapping(index)
                          }}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                      
                      {/* Mapping details */}
                      {expandedMappings.has(index) && (
                        <div className="p-3 space-y-3 border-t">
                          {/* Suppression list selector */}
                          <div>
                            <label className="text-xs font-medium text-gray-700">Suppression List</label>
                            <select
                              value={mapping.suppression_list_id}
                              onChange={(e) => updateSuppressionMapping(index, { 
                                suppression_list_id: e.target.value,
                                suppression_column: '', // Reset when list changes
                              })}
                              className="input mt-1"
                            >
                              <option value="">Select a suppression list...</option>
                              {readySuppressionLists.map(list => (
                                <option key={list.id} value={list.id}>
                                  {list.name} ({list.row_count.toLocaleString()} rows)
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          {mapping.suppression_list_id && (
                            <>
                              {/* Column mapping */}
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs font-medium text-gray-700">
                                    Suppression Column
                                  </label>
                                  <select
                                    value={mapping.suppression_column}
                                    onChange={(e) => updateSuppressionMapping(index, { 
                                      suppression_column: e.target.value 
                                    })}
                                    className="input mt-1"
                                  >
                                    <option value="">Select column...</option>
                                    {getSuppressionColumns(mapping.suppression_list_id).map(col => (
                                      <option key={col.id} value={col.name}>
                                        {col.name}
                                      </option>
                                    ))}
                                  </select>
                                  <p className="text-xs text-gray-400 mt-1">Column from suppression list</p>
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-gray-700">
                                    Dataset Column
                                  </label>
                                  <select
                                    value={mapping.dataset_column}
                                    onChange={(e) => updateSuppressionMapping(index, { 
                                      dataset_column: e.target.value 
                                    })}
                                    className="input mt-1"
                                  >
                                    <option value="">Select column...</option>
                                    {dataset.columns.map(col => (
                                      <option key={col.id} value={col.name}>
                                        {col.name}
                                      </option>
                                    ))}
                                  </select>
                                  <p className="text-xs text-gray-400 mt-1">Column to match against</p>
                                </div>
                              </div>
                              
                              {/* Match type */}
                              <div>
                                <label className="text-xs font-medium text-gray-700">Match Type</label>
                                <select
                                  value={mapping.match_type}
                                  onChange={(e) => updateSuppressionMapping(index, { 
                                    match_type: e.target.value as 'exact' | 'contains' | 'domain'
                                  })}
                                  className="input mt-1"
                                >
                                  <option value="exact">Exact Match</option>
                                  <option value="contains">Contains</option>
                                  <option value="domain">Domain Match (for emails)</option>
                                </select>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Footer */}
          <div className="flex justify-end gap-3 p-4 border-t">
            <button onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={() => exportMutation.mutate()}
              disabled={!name || selectedColumns.length === 0 || exportMutation.isPending}
              className="btn-primary"
            >
              {exportMutation.isPending ? 'Creating...' : 'Start Export'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
